import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  CircularProgress,
  useTheme,
  Dialog,
  DialogContent,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { useAppSelector } from '../store/hooks';
import type { RootState } from '../store/store';
import { servicePredictorApiService } from '../services/servicePredictorApiService';
import { findBestLCCAndPathloss } from '../utils/sectorUtils';
import type { RemoteNodeResult, SectorBaseNode, Sector } from '../store/types';

interface RemoteNodeInfo {
  remote: RemoteNodeResult;
  heightKey: string; // Format: "above_roof_above_open" (e.g., "3_10")
  heightDisplay: string; // Display format (e.g., "3 m / 10 m")
  LCC: number;
  pathLoss: string;
  linkSpeed: string;
  profileUrl: string | null;
}

const getHeightKey = (remoteNode: RemoteNodeResult): string => {
  const aboveRoof = remoteNode?.rn?.heights?.above_roof ?? 0;
  const aboveOpen = remoteNode?.rn?.heights?.above_open ?? 0;
  return `${aboveRoof}_${aboveOpen}`;
};

const getHeightDisplay = (remoteNode: RemoteNodeResult): string => {
  const aboveRoof = remoteNode?.rn?.heights?.above_roof ?? 0;
  const aboveOpen = remoteNode?.rn?.heights?.above_open ?? 0;
  return `${aboveRoof} m / ${aboveOpen} m`;
};

const getPathLoss = (remoteNode: RemoteNodeResult): string => {
  const pathLoss = remoteNode?.aiml_output?.path_loss?.average;
  if (pathLoss !== undefined) {
    return `${Math.round(pathLoss)} dB`;
  }
  return '';
};

interface CarrierInfo {
  frequency: number;
  bandwidth?: number;
  tx_power?: number;
}

interface BaseNodeWithCarrier {
  carrier?: CarrierInfo[];
}

const calculateSpeed = (
  remoteNode: RemoteNodeResult,
  baseNode: BaseNodeWithCarrier | undefined
): string => {
  const pathLoss = remoteNode?.aiml_output?.path_loss?.average;
  if (!pathLoss || !baseNode?.carrier || baseNode.carrier.length === 0) {
    return '0 Mbps';
  }

  const carrier = baseNode.carrier[0]?.frequency || 0;
  let calculatedSpeed = 0;

  if (carrier < 5000) {
    calculatedSpeed = Math.min(
      Math.max((640.0 * (160.0 - pathLoss)) / (160.0 - 133.0), 0.0),
      640.0
    );
  } else {
    calculatedSpeed = Math.min(
      Math.max((640.0 * (148.0 - pathLoss)) / (148.0 - 123.0), 0.0),
      640.0
    );
  }

  return `${Math.round(calculatedSpeed)} Mbps`;
};

const sortByHeight = (remoteNodes: RemoteNodeInfo[]): RemoteNodeInfo[] => {
  return [...remoteNodes].sort((a, b) => {
    const [aRoof, aOpen] = a.heightKey.split('_').map(Number);
    const [bRoof, bOpen] = b.heightKey.split('_').map(Number);

    if (aRoof !== bRoof) {
      return aRoof - bRoof;
    }
    return aOpen - bOpen;
  });
};

interface PathProfilePanelProps {
  onClose?: () => void;
}

const PathProfilePanel = ({ onClose }: PathProfilePanelProps) => {
  const theme = useTheme();
  const sectorResults = useAppSelector(
    (state: RootState) => state.servicePredictor.sectorResults
  );
  const baseNode = useAppSelector(
    (state: RootState) => state.servicePredictor.baseNode
  );
  const activeHeight = useAppSelector(
    (state: RootState) => state.servicePredictor.filters.activeHeight
  );

  const [selectedHeightKey, setSelectedHeightKey] = useState<string | null>(
    null
  );
  const [svgData, setSvgData] = useState<string | null>(null);
  const [loadingSvg, setLoadingSvg] = useState(false);
  const [enlargeDialogOpen, setEnlargeDialogOpen] = useState(false);

  const remoteNodeList = useMemo(() => {
    if (!sectorResults) return null;

    if (sectorResults.sector_list && Array.isArray(sectorResults.sector_list)) {
      const matchingSector = sectorResults.sector_list.find(
        (sector: Sector) => sector.bn?.serial === baseNode.serial
      );
      return matchingSector?.rn_list || null;
    }

    if (
      sectorResults.bn?.serial === baseNode.serial &&
      sectorResults.rn_list &&
      Array.isArray(sectorResults.rn_list)
    ) {
      return sectorResults.rn_list;
    }

    return null;
  }, [sectorResults, baseNode.serial]);

  const remoteNodeInfos = useMemo(() => {
    if (
      !remoteNodeList ||
      !Array.isArray(remoteNodeList) ||
      remoteNodeList.length === 0
    ) {
      return [];
    }

    const infos: RemoteNodeInfo[] = remoteNodeList
      .filter((remote: RemoteNodeResult) => {
        const LCC = remote?.aiml_output?.link_close_confidence ?? 0;
        return LCC >= 0.25;
      })
      .map((remote: RemoteNodeResult) => {
        const heightKey = getHeightKey(remote);
        const heightDisplay = getHeightDisplay(remote);
        const LCC = remote?.aiml_output?.link_close_confidence ?? 0;
        const pathLoss = getPathLoss(remote);
        const baseNodeData = sectorResults?.sector_list
          ? sectorResults.sector_list.find(
              (s: Sector) => s.bn?.serial === baseNode.serial
            )?.bn
          : sectorResults?.bn;
        const linkSpeed = calculateSpeed(remote, baseNodeData);
        const profileUrl = remote?.profile_url || null;

        return {
          remote,
          heightKey,
          heightDisplay,
          LCC,
          pathLoss,
          linkSpeed,
          profileUrl,
        };
      });

    return sortByHeight(infos);
  }, [remoteNodeList, sectorResults, baseNode]);

  const selectedRemoteInfo = useMemo(() => {
    if (remoteNodeInfos.length === 0) return null;

    if (activeHeight) {
      const matching = remoteNodeInfos.find(
        (info) => info.heightKey === activeHeight
      );
      if (matching) return matching;
    }

    if (selectedHeightKey) {
      const matching = remoteNodeInfos.find(
        (info) => info.heightKey === selectedHeightKey
      );
      if (matching) return matching;
    }

    const defaultHeight = remoteNodeInfos.find(
      (info) => info.heightKey === '3_10'
    );
    if (defaultHeight) return defaultHeight;

    return findBestLCCAndPathloss(remoteNodeInfos);
  }, [remoteNodeInfos, activeHeight, selectedHeightKey]);

  useEffect(() => {
    setSelectedHeightKey(null);
    setSvgData(null);
  }, [baseNode.serial]);

  useEffect(() => {
    if (activeHeight && remoteNodeInfos.length > 0) {
      const matching = remoteNodeInfos.find(
        (info) => info.heightKey === activeHeight
      );
      if (matching) {
        setSelectedHeightKey(activeHeight);
      }
    }
  }, [activeHeight, remoteNodeInfos]);

  useEffect(() => {
    if (remoteNodeInfos.length > 0 && !selectedHeightKey && !activeHeight) {
      const best = findBestLCCAndPathloss(remoteNodeInfos);
      if (best) {
        setSelectedHeightKey(best.heightKey);
      }
    }
  }, [remoteNodeInfos, selectedHeightKey, activeHeight]);

  useEffect(() => {
    if (!selectedRemoteInfo?.profileUrl) {
      setSvgData(null);
      return;
    }

    let cancelled = false;

    const fetchSvg = async () => {
      setLoadingSvg(true);
      try {
        const response = await servicePredictorApiService.getSVGData({
          url: selectedRemoteInfo.profileUrl!,
        });

        if (cancelled) return;

        if (typeof response === 'string') {
          setSvgData(response);
        } else if (response.canceled) {
          return;
        }
      } catch (error) {
        console.error('Error fetching SVG data:', error);
        if (!cancelled) {
          setSvgData(null);
        }
      } finally {
        if (!cancelled) {
          setLoadingSvg(false);
        }
      }
    };

    fetchSvg();

    return () => {
      cancelled = true;
    };
  }, [selectedRemoteInfo?.profileUrl]);

  const handleHeightSelect = useCallback((heightKey: string) => {
    setSelectedHeightKey(heightKey);
  }, []);

  const handleEnlarge = useCallback(() => {
    setEnlargeDialogOpen(true);
  }, []);

  const handleCloseEnlarge = useCallback(() => {
    setEnlargeDialogOpen(false);
  }, []);

  const renderLeftColumn = () => (
    <Box
      sx={{
        flex: '15%',
        minWidth: '250px',
        maxWidth: '300px',
        borderRight: `1px solid ${theme.palette.divider}`,
        overflowY: 'auto',
        p: 3,
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
        Height Options
      </Typography>
      {remoteNodeInfos.map((info) => {
        const isSelected = selectedRemoteInfo?.heightKey === info.heightKey;
        return (
          <Box
            key={info.heightKey}
            onClick={() => handleHeightSelect(info.heightKey)}
            sx={{
              p: 1.5,
              mb: 1,
              borderRadius: 1,
              cursor: 'pointer',
              backgroundColor: isSelected
                ? theme.palette.primary.main
                : theme.palette.background.paper,
              color: isSelected
                ? theme.palette.primary.contrastText
                : theme.palette.text.primary,
              border: `1px solid ${
                isSelected ? theme.palette.primary.main : theme.palette.divider
              }`,
              '&:hover': {
                backgroundColor: isSelected
                  ? theme.palette.primary.dark
                  : theme.palette.action.hover,
              },
              transition: 'all 0.2s',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Height: {info.heightDisplay}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
              Link Close Confidence: {(info.LCC * 100).toFixed(0)}%
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
              Predicted Path Loss: {info.pathLoss || 'N/A'}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Link Speed: {info.linkSpeed}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );

  const renderRightColumn = () => (
    <Box
      sx={{
        flex: '85%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
      }}
    >
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 1,
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 1)',
          },
        }}
        aria-label="close"
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      {loadingSvg ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
          }}
        >
          <CircularProgress />
        </Box>
      ) : svgData ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'auto',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              '& svg': {
                cursor: 'pointer',
              },
            }}
            onClick={handleEnlarge}
            dangerouslySetInnerHTML={{ __html: svgData }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
              zIndex: 1,
            }}
          >
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                handleEnlarge();
              }}
              size="small"
              aria-label="enlarge"
              sx={{
                backgroundColor: 'rgba(35, 31, 32, 0.9)',
                color: '#f8f8f8',
                '&:hover': {
                  backgroundColor: 'rgba(35, 31, 32, 1)',
                },
              }}
            >
              <FullscreenIcon />
            </IconButton>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            color: theme.palette.text.secondary,
          }}
        >
          <Typography variant="body2">No profile data available</Typography>
        </Box>
      )}
    </Box>
  );

  const renderEnlargeDialog = () => (
    <Dialog
      open={enlargeDialogOpen}
      onClose={handleCloseEnlarge}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: '90%',
          maxWidth: '1200px',
          maxHeight: '95vh',
          m: 2,
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogContent
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'auto',
          flex: 1,
          minHeight: 0,
        }}
      >
        <IconButton
          onClick={handleCloseEnlarge}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 1,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 1)',
            },
          }}
          aria-label="close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        {svgData && (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': {
                width: 'auto',
                height: 'auto',
                maxWidth: '100%',
                maxHeight: '100%',
              },
            }}
            dangerouslySetInnerHTML={{ __html: svgData }}
          />
        )}
      </DialogContent>
    </Dialog>
  );

  if (!baseNode.serial || remoteNodeInfos.length === 0) {
    return null;
  }

  return (
    <>
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          width: '95%',
          maxWidth: '1600px',
          maxHeight: '600px',
          minHeight: '550px',
          zIndex: 1400,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          {renderLeftColumn()}
          {renderRightColumn()}
        </Box>
      </Paper>
      {renderEnlargeDialog()}
    </>
  );
};

export default PathProfilePanel;
